import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Home, CheckCircle } from 'lucide-react';

const resetPasswordSchema = z.object({
  password: z.string().min(6, { message: 'Password must be at least 6 characters' }),
  confirmPassword: z.string().min(6, { message: 'Password must be at least 6 characters' }),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

const ResetPassword = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { updatePassword, user, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const form = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const [exchangeFailed, setExchangeFailed] = useState(false);

  // Capture, on first render, the recovery credentials the link landed with.
  // A Supabase recovery link arrives in one of a few shapes:
  //   - implicit grant:   #access_token=...&type=recovery   (hash)
  //   - "verify" OTP flow: ?token_hash=...&type=recovery     (query)
  //   - PKCE:             ?code=...                          (query)
  // We read these synchronously on first render because the SDK strips the hash
  // out of the URL as soon as it detects a session.
  const [recoveryParams] = useState(() => {
    if (typeof window === 'undefined') {
      return { hasCredentials: false, tokenHash: null as string | null, type: null as string | null, code: null as string | null };
    }
    const query = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const tokenHash = query.get('token_hash');
    const code = query.get('code');
    const type = query.get('type') ?? hash.get('type');
    const hasCredentials = Boolean(hash.get('access_token') || tokenHash || code);
    return { hasCredentials, tokenHash, type, code };
  });

  // The Supabase SDK auto-detects the implicit-grant hash on load, but it does
  // NOT consume a `token_hash`/`code` query param on its own — so without this
  // exchange the recovery link never establishes a session and the page bounces
  // to /auth. Exchange it explicitly; AuthContext then picks the session up via
  // onAuthStateChange.
  useEffect(() => {
    const { tokenHash, type, code } = recoveryParams;
    if (!tokenHash && !code) return;

    let active = true;
    (async () => {
      const { error } = tokenHash
        ? await supabase.auth.verifyOtp({ type: (type ?? 'recovery') as 'recovery', token_hash: tokenHash })
        : await supabase.auth.exchangeCodeForSession(code as string);

      if (!active) return;
      if (error) {
        setExchangeFailed(true);
        return;
      }
      // Drop the now-consumed token from the URL so a refresh can't re-run it.
      window.history.replaceState(window.history.state, '', '/reset-password');
    })();

    return () => {
      active = false;
    };
  }, [recoveryParams]);

  // Wait for auth to load and check if we have a valid session from the reset link.
  useEffect(() => {
    if (loading || user) return;
    // While a recovery link is establishing its session from the URL tokens,
    // `user` is briefly null even after `loading` finishes. Don't bounce to
    // /auth in that window — only once the exchange has actually failed, or the
    // page was opened with no recovery credentials at all.
    if (recoveryParams.hasCredentials && !exchangeFailed) return;

    toast({
      title: 'Invalid or expired link',
      description: 'Please request a new password reset link.',
      variant: 'destructive',
    });
    navigate('/auth');
  }, [loading, user, recoveryParams, exchangeFailed, navigate, toast]);

  const onSubmit = async (data: ResetPasswordFormData) => {
    setIsSubmitting(true);
    
    try {
      const { error } = await updatePassword(data.password);
      if (error) {
        toast({
          title: 'Password reset failed',
          description: error.message,
          variant: 'destructive',
        });
      } else {
        setIsSuccess(true);
        toast({
          title: 'Password updated',
          description: 'Your password has been successfully reset.',
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <CardTitle className="text-2xl font-display">Password Reset Complete</CardTitle>
            <CardDescription>
              Your password has been successfully updated.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={() => navigate('/admin')}>
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-display">Reset Password</CardTitle>
          <CardDescription>
            Enter your new password below
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>New Password</FormLabel>
                    <FormControl>
                      <Input type="password" placeholder="••••••••" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm Password</FormLabel>
                    <FormControl>
                      <Input type="password" placeholder="••••••••" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : null}
                Reset Password
              </Button>
            </form>
          </Form>

          <div className="mt-6 pt-4 border-t border-border">
            <Button variant="outline" className="w-full" asChild>
              <Link to="/">
                <Home className="h-4 w-4 mr-2" />
                Back to Homepage
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
